import { useFormik } from "formik";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";
import {
  ChatAPIs,
  ChatApiTags,
  useLazySMSUsersQuery,
  useSendSMSMutation,
  useSMSUsersQuery,
} from "../../api/network/chatApiServices";
import { serializeErrorKeyValues } from "../../api/network/errorCodes";
import { setModalsData, TModalsState } from "../../api/store/commonSlice";
import { SendSMSPayload, SMSUser } from "../../api/types/Chat";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../api/types/Onboarding";
import { LegacyUserEntityListingPayload } from "../../api/types/User";
import {
  AdminFormFieldAsyncDropdown,
  AdminFormFieldAsyncLazyDropdown,
  AdminFormFieldPillItem,
  AdminFormFieldPillList,
  AdminFormFieldSubmit,
  AdminFormFieldTextarea,
  PillItem,
  TSelectboxOption,
} from "../../components/admin/formFields";
import { APP_CONFIG } from "../../constants/constants";
import { useLoggedInUserData } from "../../utils/user";
import { useChatContext } from "./contexts/chat.context";
import {
  createNewGroupSMSInitialValues,
  createNewGroupSMSValidationSchema,
  TFormFieldNamesSMS,
} from "./validation";
import { ACL_NAMES, useACL } from "../../hooks/acl";
import { ROLE_ACCESS_LEVELS } from "../admin-details/role/constants";
import FormActionButtons from "../../components/admin/formActionButtons";
import { asyncDebounce } from "../../utils/common";
import { GroupBase, OptionsOrGroups } from "react-select";

type ModalCreateNewSMSGroupProps = {};

const ModalCreateNewSMSGroup: FC<ModalCreateNewSMSGroupProps> = () => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "smsSection.create_new",
  });
  const dispatch = useDispatch();
  const { getUserACLAccessLevel } = useACL();
  const thisOrgDriverGroupAllowed = useLoggedInUserData("hasdrivers");
  const { setSelectedChatChannel } = useChatContext();

  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const loggedInUserData = useSelector(
    (state: { commonReducer: { user: LegacyVerifyEmailOtpResponseSuccess } }) =>
      state.commonReducer.user
  );

  // send sms mutation
  const [sendSMSAPITrigger, { isLoading: isLoadingSendSMS }] =
    useSendSMSMutation();

  //formik
  const formik = useFormik({
    initialValues: createNewGroupSMSInitialValues,
    validationSchema: createNewGroupSMSValidationSchema,
    onSubmit: (values) => {
      //prepare payload
      const payload: SendSMSPayload = {
        members: values.selected_users.map((item)=> typeof item === "string" ? item : item?.value),
        message: values.sms_message,
      };
      const timeoutMultiplier = 200;
      const timeoutDuration = values.selected_users.length * timeoutMultiplier;
      sendSMSAPITrigger(payload)
        .unwrap()
        .then((data) => {
          formik.resetForm();
          if (!!data?.message) {
            // changes the checkcase condition
            dispatch(
              setModalsData({ ...modalsState, showCreateGroupSMSModal: false })
            );
            setTimeout(() => {
              dispatch(ChatAPIs.util.invalidateTags([ChatApiTags.GetSMS]));
              dispatch(ChatAPIs.util.invalidateTags([ChatApiTags.SendSMS]));
            }, timeoutDuration);
            toast.success(t("toast.send_success"));
          } else {
            toast.error(t("toast.send_failed"));
          }
        })
        .catch((error) => {
          const errors = !!error?.data
            ? serializeErrorKeyValues(error?.data)
            : [t("toast.send_failed")];
          toast.error(errors?.join(" "));
        });
    },
  });

  //formik
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    dirty,
    setFieldValue,
    setFieldTouched,
    resetForm,
    submitForm,
  } = formik;

  const invalidFields = Object.keys(errors).filter(
    (key) => !!errors[key as TFormFieldNamesSMS]
  );
  const isFormValid = invalidFields.length === 0 && dirty;
  const MemoizedFormActionButtons = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              label: t("submit_button"),
              variant: "primary",
              disabled:
                !isFormValid ||
                isLoadingSendSMS ||
                values.selected_users.length === 0,
            },
            {
              label: tMain("cancel"),
              type: "button",
              variant: "danger-transparent",
              disabled: isLoadingSendSMS,
              onClick: () => hideModal(),
            },
          ]}
        />
      </>
    );
  }, [isFormValid, isLoadingSendSMS, values.selected_users]);
  const hideModal = () => {
    formik.resetForm();
    dispatch(setModalsData({ ...modalsState, showCreateGroupSMSModal: false }));
  };

  // prepare user query params
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const [orgUsersQueryParams, setOrgUsersQueryParams] =
    useState<LegacyUserEntityListingPayload>({
      accountid: `${thisUserOrganizationId}`,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.LARGE_PAGE_SIZE,
      search: "",
    });

  // fetch organization users
  // const { data: dataSMSUsers, isFetching: isFetchingSMSUsers } =
  //   useSMSUsersQuery({});

  const [searchSMSUserAPITrigger, { isLoading: isLoadingSMSUsers }] =
    useLazySMSUsersQuery();

  const loadOptionsHandlerUser = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    const { data: driversQueryResponse } = await searchSMSUserAPITrigger({
      search: inputValue,
      page: additional?.page ?? 2, // start from page 2 as page 1 is already loaded from addition prop of component
      // page_size: APP_CONFIG.LISTINGS.DROPDOWN_SEARCH_PAGE_SIZE,
    });

    if (!driversQueryResponse || !driversQueryResponse?.results) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: 2,
        },
      };
    }
    const { results } = driversQueryResponse as { results: SMSUser[] };
    return {
      options: !!results
        ? (results || ([] as SMSUser[]))
            .filter(
              (item: SMSUser) =>
                item.driverid !== loggedInUserData?.user?.userid
            ) // exclude current user
            .filter(
              (item: SMSUser) =>
                !values.selected_users.map((item)=> typeof item === "string" ? item : item?.value).includes(`${item.driverid}`)
            ) // exclude already selected users
            .map(
              (item: SMSUser, index: number) =>
                ({
                  value: `${item.driverid}`,
                  label: `${item.description || item.driverid}`,
                }) as TSelectboxOption
            )
        : [],
      hasMore: driversQueryResponse?.next ? true : false,
      additional: {
        page: additional?.page ? (additional?.page as number) + 1 : 2,
      },
    };
  };
  const debouncedLoadOptionsHandlerUser = asyncDebounce(
    loadOptionsHandlerUser,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  if (modalsState.showCreateGroupSMSModal === false) return null;
  return (
    <>
      <div className="justify-center items-start flex overflow-x-hidden overflow-y-auto fixed inset-0 z-overlay outline-none focus:outline-none">
        {/* <div
          className="fixed w-full h-screen bg-modal-overlay z-overlay"
          onClick={hideModal}
        ></div> */}
        <div className="relative my-6 mx-auto max-w-[calc(100vw-4rem)] w-[480px] z-modal">
          <form
            className="p-8 bg-gray-100 grid grid-cols-6 gap-6 rounded-3xl shadow-2xl"
            onSubmit={handleSubmit}
          >
            <h2 className="col-span-6 text-2xl font-bold text-heading-black">
              {t("heading")}
            </h2>
            <>
              <div className="col-span-6 grid grid-cols-6 gap-6">
                <AdminFormFieldAsyncLazyDropdown
                  key={`user_selector_`} // causes re-render on group selection
                  loadingData={isLoadingSMSUsers}
                  label={t("select_user_to_add")}
                  id="users"
                  name="users"
                  isMulti={true}
                  value={values.selected_users as any}
                  noOptionsMessage={() => t("user_selector_no_options")}
                  // defaultOption={{ value: `${values.group_selector}`, label: `${groupDisplayName(`${values.streetaddress}`, "-")}` }}
                  // defaultOptions={dataSMSUsers?.results
                  //   ?.filter(
                  //     (item: SMSUser) =>
                  //       item.driverid !== loggedInUserData?.user?.userid
                  //   ) // exclude current user
                  //   ?.filter(
                  //     (item) =>
                  //       !values.selected_users.includes(`${item.driverid}`)
                  //   ) // exclude already selected users
                  //   ?.map((item: SMSUser) => ({
                  //     value: `${item.driverid}`,
                  //     label: `${item.description || item.driverid}`,
                  //   }))}
                  placeholder={t("select_user_to_add_placeholder")}
                  loadOptions={debouncedLoadOptionsHandlerUser}
                  onChange={(e:any) => {
                    if (!!e) {
                      const newlyAdded = e
                      setFieldValue("selected_users", [
                       ...newlyAdded
                      ]);
                    }
                  }}
                  disabled={
                    thisOrgDriverGroupAllowed === "0" ||
                    (getUserACLAccessLevel(
                      ACL_NAMES.ACL_MESSAGING_SEND_MASSMSG
                    ) <= ROLE_ACCESS_LEVELS.READ &&
                      values.selected_users.length >= 1)
                  }
                />
                {/* <AdminFormFieldPillList>
                  {values.selected_users.map((user: string, i: number) => (
                    <AdminFormFieldPillItem // TODO: Fix prefill on change item from left column
                      key={`pillItem_${i}`}
                      item={{ name: user } as PillItem}
                      onRemove={() => {
                        setFieldValue(
                          "selected_users",
                          values.selected_users.filter(
                            (selectedUser: any) => selectedUser !== user
                          )
                        );
                      }}
                      disabled={false}
                    />
                  ))}
                </AdminFormFieldPillList> */}
              </div>

              <AdminFormFieldTextarea
                label={t("sms_message")}
                id="SMSMessage"
                name="sms_message"
                placeholder={t("sms_message_placeholder")}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.sms_message}
                touched={touched.sms_message}
                error={errors.sms_message}
              />
              {MemoizedFormActionButtons}
              {/* <AdminFormFieldSubmit
                label={t('submit_button')}
                variant="primary"
                disabled={!isFormValid || isLoadingSendSMS || values.selected_users.length === 0}
              />

              <AdminFormFieldSubmit
                label={tMain('cancel')}
                type="button"
                variant="danger-transparent"
                disabled={isLoadingSendSMS}
                onClick={hideModal}
              /> */}
            </>
          </form>
        </div>
      </div>
    </>
  );
};

export default ModalCreateNewSMSGroup;
